import React, { useState, useMemo, useCallback } from "react";
import {
  AccountTreeGroup,
  AccountTreeNode,
} from "../../domain/entities/AccountTreeEntity";
import { GLMappingEntity } from "../../domain/entities/GLMappingEntity";
import { formatAccountTypeLabel } from "../../utils/glTreeUtils";
import { GlTreePickerShell } from "./GlTreePickerShell";
import { GlTreePickerHookState } from "../types/glTreePickerTypes";

const DEFAULT_EXCLUDED_ACCOUNT_TYPES = [
  "bank",
  "accounts_receivable",
  "accounts_payable",
];

export interface GlAccountTypePickerProps {
  groups: Record<string, string[]>;
  /** Optional slug → display label (from account-type-groups API). */
  labels?: Record<string, string>;
  selectedAccountType?: string;
  excludedAccountTypes?: string[];
  onSelect: (accountType: string) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  dropdownClassName?: string;
  placeholder?: string;
}

function slugifyCategory(label: string): string {
  return label.toLowerCase().replace(/\s+/g, "_");
}

function resolveTypeLabel(
  type: string,
  labels?: Record<string, string>,
): string {
  return labels?.[type] ?? formatAccountTypeLabel(type);
}

function buildAccountTypeGroups(
  groups: Record<string, string[]>,
  excluded: Set<string>,
  labels?: Record<string, string>,
): AccountTreeGroup[] {
  const result: AccountTreeGroup[] = [];

  for (const categoryLabel of Object.keys(groups)) {
    const types = groups[categoryLabel] ?? [];
    const items = types.filter((type: string) => !excluded.has(type));
    if (items.length === 0) {
      continue;
    }

    const nodes: AccountTreeNode[] = items.map((type: string) => {
      const label = resolveTypeLabel(type, labels);
      return {
        account_id: type,
        gl_id: type,
        gl_name: label,
        full_path: label,
        account_type: type,
        parent_id: null,
        has_children: false,
        is_selectable: true,
        leaf_count: 0,
        children: [],
      };
    });

    result.push({
      account_type: slugifyCategory(categoryLabel),
      label: categoryLabel,
      total_leaves: items.length,
      nodes,
    });
  }

  return result;
}

function filterGroupsBySearch(
  groups: AccountTreeGroup[],
  search: string,
): AccountTreeGroup[] {
  const q = search.trim().toLowerCase();
  if (!q) {
    return groups;
  }

  return groups
    .map((group) => {
      const nodes = group.nodes.filter(
        (node) =>
          node.gl_name.toLowerCase().includes(q) ||
          node.account_type.toLowerCase().includes(q),
      );
      if (nodes.length === 0) {
        return null;
      }
      return {
        ...group,
        nodes,
        total_leaves: nodes.length,
      } satisfies AccountTreeGroup;
    })
    .filter((group): group is AccountTreeGroup => group !== null);
}

function useGlAccountTypePickerTree({
  groups,
  labels,
  selectedAccountType,
  excludedAccountTypes = DEFAULT_EXCLUDED_ACCOUNT_TYPES,
  enabled = true,
}: {
  groups: Record<string, string[]>;
  labels?: Record<string, string>;
  selectedAccountType?: string;
  excludedAccountTypes?: string[];
  enabled?: boolean;
}): GlTreePickerHookState {
  const [searchTerm, setSearchTermState] = useState("");

  const excludedSet = useMemo(
    () => new Set(excludedAccountTypes),
    [excludedAccountTypes],
  );

  const baseGroups = useMemo(
    () => (enabled ? buildAccountTypeGroups(groups, excludedSet, labels) : []),
    [enabled, groups, excludedSet, labels],
  );

  const searchActive = searchTerm.trim().length > 0;

  const filteredGroups = useMemo(
    () =>
      searchActive
        ? filterGroupsBySearch(baseGroups, searchTerm)
        : baseGroups,
    [baseGroups, searchActive, searchTerm],
  );

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const loadAccountType = useCallback(async (_type: string): Promise<void> => {
    // All account types are preloaded in each category group.
  }, []);

  const loadMore = useCallback(() => {
    // Client-side only — no pagination.
  }, []);

  return {
    groups: filteredGroups,
    loading: false,
    loadingMore: false,
    loadingTypes: new Set(),
    hasMore: false,
    searchTerm,
    searchActive,
    selectedAccountType: selectedAccountType ?? null,
    setSearchTerm,
    loadAccountType,
    loadMore,
    error: null,
  };
}

export function GlAccountTypePicker({
  groups,
  labels,
  selectedAccountType,
  excludedAccountTypes,
  onSelect,
  disabled = false,
  loading = false,
  className = "",
  dropdownClassName = "",
  placeholder = "Select account type",
}: GlAccountTypePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const treeState = useGlAccountTypePickerTree({
    groups,
    labels,
    selectedAccountType,
    excludedAccountTypes,
    enabled: isOpen && !disabled && !loading,
  });

  const handleSelect = (account: GLMappingEntity) => {
    onSelect(account.account_id);
  };

  const selectedLabel = selectedAccountType
    ? resolveTypeLabel(selectedAccountType, labels)
    : undefined;

  return (
    <GlTreePickerShell
      treeState={{
        ...treeState,
        loading: loading || treeState.loading,
      }}
      selectedAccountId={selectedAccountType}
      selectedAccountName={selectedLabel}
      onSelect={handleSelect}
      disabled={disabled || loading}
      className={className}
      dropdownClassName={dropdownClassName}
      placeholder={placeholder}
      searchPlaceholder="Search account types…"
      emptyMessage="Loading account types…"
      noResultsMessage="No account types match"
      onOpenChange={setIsOpen}
    />
  );
}

export default GlAccountTypePicker;
